import React from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Outlet, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import { CCButton, CCEmptyState, CCSpinner } from "@pymblecitizencoders/cc-ui";
import { CCSignInCard } from "@pymblecitizencoders/cc-ui/auth";
import { useMyDetails } from "../hooks/useMyDetails";
import AccessDenied from "../components/AccessDenied";
import { AdminNav } from "../components/eventManager/AdminNav";
import { app, loginRequest } from "../config";
import "./layout.css";
import "../components/eventManager/eventManager.css";

/**
 * Gates children behind an active-staff check against the S2 API.
 * The API enforces authorisation independently — this is UI only.
 */
const AuthGate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
	const { isLoading, isError, hasAdminAccess } = useMyDetails();

	if (isLoading)
		return (
			<div className="em-centre-block">
				<CCSpinner size="lg" label="Checking access..." />
			</div>
		);

	if (isError)
		return (
			<div className="em-centre-block">
				<CCEmptyState message="Error loading user details. Please refresh." />
			</div>
		);

	if (!hasAdminAccess) return <AccessDenied />;

	return <>{children}</>;
};

export const PageLayout = () => {
	const { instance, accounts, inProgress } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	// MSAL is mid-redirect: showing the sign-in card here would let the user
	// kick off a second interaction and hit interaction_in_progress.
	const handlingRedirect =
		inProgress === InteractionStatus.HandleRedirect ||
		inProgress === InteractionStatus.Startup;

	const handleLogin = () => {
		if (inProgress !== InteractionStatus.None) return;
		instance.loginRedirect(loginRequest).catch((err) => {
			if (err?.errorCode === "interaction_in_progress") {
				// Stale lock left in sessionStorage by an incomplete redirect —
				// clear it and retry rather than stranding the user.
				Object.keys(sessionStorage)
					.filter((k) => k.includes("interaction.status"))
					.forEach((k) => sessionStorage.removeItem(k));
				instance.loginRedirect(loginRequest).catch(console.error);
			} else {
				console.error(err);
			}
		});
	};

	if (!isAuthenticated) {
		if (handlingRedirect) {
			return (
				<div className="em-centre-block">
					<CCSpinner size="lg" label="Signing in..." />
				</div>
			);
		}

		return (
			<div className="em-login-screen">
				<CCSignInCard
					title={app.name}
					message="Sign in to manage school events, permissions and attendees."
					onSignIn={handleLogin}
				/>
			</div>
		);
	}

	return (
		<>
			{/* Three equal cells: brand, title, account. Was a Bootstrap Navbar
			    with col-4 children, but .em-navbar already owned every visible
			    part of it (colour, height, the red rule, padding) — all Bootstrap
			    contributed was `display: flex`, which is now in the stylesheet. */}
			<header className="em-navbar">
				<div className="em-navbar-cell">
					<Link className="em-navbar-brand" to="/">
						<img
							src={app.logoUrl}
							alt="Pymble Ladies College Logo"
							className="header-logo"
						/>
					</Link>
				</div>
				<div className="em-navbar-cell em-navbar-cell--center">
					<h2 className="main-title">{app.name}</h2>
				</div>
				<div className="em-navbar-cell align-right em-navbar-account">
					<AdminNav />
					<span>
						Hi {accounts[0]?.name}!
						<CCButton
							variant="clear"
							className="em-navbar-signout"
							title="Sign out"
							onClick={() => instance.logoutRedirect()}
						>
							<FontAwesomeIcon icon={faRightFromBracket} />
						</CCButton>
					</span>
				</div>
			</header>
			<AuthGate>
				<Outlet />
			</AuthGate>
		</>
	);
};
